// The scene catalog: which picture goes with which game moment.
//
// One table, so a panel never hard-codes a path and a missing file is caught by
// `__tests__/scenes.test.ts`. Files live under public/assets/images/<group>/;
// each has a `--<w>` thumbnail beside it (scripts/generate-images.mjs makes both).

const BASE = '/assets/images';

export const SCENES = {
  boss: ['dragon', 'golem', 'lich', 'giant', 'hydra', 'chimera', 'medusa'],
  spy: ['spy-infiltrate', 'spy-report', 'spy-caught', 'spy-rescue', 'spy-sabotage'],
  spyAct: [
    'act-intel-success',
    'act-intel-failure',
    'act-sabotage-success',
    'act-sabotage-failure',
    'act-assassination-success',
    'act-assassination-failure',
    'act-heist-success',
    'act-heist-failure',
    'act-spy-turned',
    'act-spy-executed',
  ],
  spyPersona: [
    'persona-shadow',
    'persona-courtesan',
    'persona-urchin',
    'persona-spymaster',
    'persona-assassin',
    'persona-double-agent',
  ],
  diplomacy: ['pact', 'alliance', 'peace', 'war-declared', 'betrayal', 'marriage'],
  toast: ['city-assaulted', 'city-sacked', 'merchant-docked', 'trade-route-opened'],
  milestone: [
    'city-founded',
    'city-tier',
    'city-captured',
    'hero-recruited',
    'doctrine-tier',
    'temple-built',
    'wonder-built',
    'ritual-completed',
    'decisive-battle',
    'era-advanced',
  ],
  research: ['breakthrough-discovery', 'breakthrough-insight'],
  faith: ['schism', 'heretic-uprising', 'holy-war'],
  artifact: [
    'artifact-industry',
    'artifact-science',
    'artifact-faith',
    'artifact-arcane',
    'artifact-nature',
    'artifact-chaos',
  ],
  royal: [
    'royal-wedding-feast',
    'royal-betrothal',
    'royal-assassinated',
    'royal-poison-foiled',
    'royal-captured',
    'royal-ransom',
    'royal-rescue',
    'royal-escape',
    'royal-homecoming',
    'royal-heir-born',
    'royal-coronation',
    'royal-marriage-void',
    'royal-hostage-executed',
    'royal-exile',
  ],
  noble: [
    'noble-wedding',
    'noble-wedding-grand',
    'royal-wedding-procession',
    'prince-knighting',
    'prince-hunt',
    'prince-duel',
    'prince-captured',
    'princess-betrothal',
    'princess-abducted',
    'princess-court',
    'noble-intrigue',
    'noble-tournament',
  ],
  victory: [
    'victory-conquest',
    'victory-economy',
    'victory-science',
    'victory-faith',
    'victory-diplomacy',
    'victory-milestones',
    'victory-conquest-2',
    'victory-economy-2',
    'victory-science-2',
    'victory-faith-2',
    'victory-diplomacy-2',
    'victory-milestones-2',
  ],
  defeat: ['defeat-ruin', 'defeat-exile', 'defeat-siege'],
  heroPortrait: [
    'champion',
    'champion_female',
    'diplomat',
    'diplomat_female',
    'sage',
    'sage_female',
    'spymaster',
    'spymaster_female',
    'tactician',
    'tactician_female',
    'warlord',
    'warlord_female',
  ],
  heroRole: ['champion', 'diplomat', 'general', 'sage', 'spymaster', 'steward'],
  heroAbility: [
    'rally',
    'steel_wall',
    'charge',
    'coordinated_strike',
    'cleave',
    'defensive_fortify',
    'insight',
    'sacred_heal',
    'tempest',
    'plant_whispers',
    'counter_spy',
    'envoy',
    'charm',
    'champion_aura',
    'stewardship',
  ],
  heroStatus: ['wounded', 'captured', 'aura_active'],
} as const;

export type SceneGroup = keyof typeof SCENES;
export type SceneId<G extends SceneGroup> = (typeof SCENES)[G][number];

/** Folder under /assets/images for each group. */
export const SCENE_DIR: Record<SceneGroup, string> = {
  boss: 'bosses',
  spy: 'spies',
  spyAct: 'spies/acts',
  spyPersona: 'spies/personas',
  diplomacy: 'diplomacy',
  toast: 'toasts',
  royal: 'royals',
  noble: 'royals/nobles',
  victory: 'victory',
  defeat: 'victory',
  heroPortrait: 'heroes/portraits',
  heroRole: 'heroes/roles',
  heroAbility: 'heroes/abilities',
  heroStatus: 'heroes/status',
  milestone: 'milestones',
  research: 'research',
  faith: 'faith',
  artifact: 'artifacts',
};

/** Width of the `--<w>` thumbnail each group ships with. */
export const THUMB_WIDTH: Record<SceneGroup, number> = {
  boss: 320,
  spy: 384,
  spyAct: 384,
  spyPersona: 128,
  diplomacy: 384,
  royal: 384,
  noble: 384,
  victory: 640,
  defeat: 640,
  heroPortrait: 256,
  heroRole: 64,
  heroAbility: 64,
  heroStatus: 64,
  toast: 320,
  milestone: 320,
  research: 320,
  faith: 320,
  artifact: 256,
};

/** Full-size and thumbnail URLs of one scene. */
export function sceneUrls<G extends SceneGroup>(
  group: G,
  id: SceneId<G>,
): { readonly src: string; readonly srcSmall: string } {
  const dir = `${BASE}/${SCENE_DIR[group]}`;
  return { src: `${dir}/${id}.webp`, srcSmall: `${dir}/${id}--${THUMB_WIDTH[group]}.webp` };
}

/** The hero portrait for an archetype ("Warlord") and a gender, or null if unknown. */
export function heroPortraitId(archetype: string, female = false): SceneId<'heroPortrait'> | null {
  const base = archetype.toLowerCase();
  const id = female ? `${base}_female` : base;
  return (SCENES.heroPortrait as readonly string[]).includes(id)
    ? (id as SceneId<'heroPortrait'>)
    : null;
}

/** The boss card for a champion kind (`dragon`, `golem`, `lich`), or null. */
export function bossSceneId(kind: string): SceneId<'boss'> | null {
  return (SCENES.boss as readonly string[]).includes(kind) ? (kind as SceneId<'boss'>) : null;
}

/** True when `id` names a scene of `group` (a server or save may send any string). */
export function isSceneId<G extends SceneGroup>(group: G, id: string): id is SceneId<G> {
  return (SCENES[group] as readonly string[]).includes(id);
}
